import { Box } from '@mui/material';
import {
  ComponentType,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { IconProps } from '@/components/icons/ChevronDownIcon';
import { Wallet01Icon } from '@/components/icons/Wallet01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  MenuItem,
  MenuItemProps,
} from '@/components/poppers/MenuPopper/MenuItem';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { useReportError } from '@/hooks/useReportError';
import { getIntegrationProviderDisplayName } from '@/modules/assetProviderIntegrations/shared/constants';
import { AssetIntegration_EntityFragment } from '@/modules/assetProviderIntegrations/shared/graphql/assetProviderIntegration.fragments.generated';
import { getEntityAssetsIntegrationProvider } from '@/modules/assetProviderIntegrations/shared/utils';
import { FullScreenStructuredAssetsModal } from '@/modules/assets/FullScreenStructuredAssetsModal/FullScreenStructuredAssetsModal';
import { AssetFullScreenModal } from '@/modules/assetValuation/AssetFullScreenModal/AssetFullScreenModal';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import {
  getActiveTabForSection,
  NAVIGATE_TO_ENTITY_TAB_AFTER_SAVE_PARAM,
} from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.utils';
import { EditEntitySplitScreenModal } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenModal';
import { LinkIntegrationEntityFormModal } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/LinkIntegrationEntityFormModal/LinkIntegrationEntityFormModal';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { CreateLiabilitySideDrawer } from '@/modules/liabilities/LiabilityModal/CreateLiabilitySideDrawer';
import { EditLiabilitySideDrawer } from '@/modules/liabilities/LiabilityModal/EditLiabilitySideDrawer';
import { COLORS } from '@/styles/tokens/colors';
import { EntityKind } from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

import { useEntityDetailsContext } from '../../contexts/entityDetails/entityDetails.context';
import { EntityDetail_EntityFragment } from '../graphql/EntityDetailPage.generated';
import { useResyncEntityValuationMutation } from './graphql/EntityValuation.generated';
import { MoveAssetsToNewOwnerSideDrawer } from './MoveAssetsToNewOwner/MoveAssetsToNewOwnerSideDrawer';

export enum EntityValuationAction {
  MANAGE_VALUATION = 'MANAGE_VALUATION',
  VIEW_ON_INTEGRATED_PLATFORM = 'VIEW_ON_INTEGRATED_PLATFORM',
  OPEN_ENTITY_EDIT_MODAL = 'OPEN_ENTITY_EDIT_MODAL',
  ADD_LIABILITY = 'ADD_LIABILITY',
  EDIT_LIABILITY = 'EDIT_LIABILITY',
  RESYNC_ENTITY_VALUATION = 'RESYNC_ENTITY_VALUATION',
  MANAGE_DATA_LINK = 'MANAGE_DATA_LINK',
  MOVE_ASSETS = 'MOVE_ASSETS',
}

export type EntityValuationActionDefinition =
  | {
      kind: EntityValuationAction.MANAGE_VALUATION;
      label: string;
      showDivider?: boolean;
      icon: ComponentType<IconProps>;
    }
  | {
      kind: EntityValuationAction.OPEN_ENTITY_EDIT_MODAL;
      initialEditEntitySection: EditEntitySection;
      label: string;
      showDivider?: boolean;
      icon: ComponentType<IconProps>;
    }
  | {
      kind: EntityValuationAction.ADD_LIABILITY;
      label: string;
      showDivider?: boolean;
      icon: ComponentType<IconProps>;
    }
  | {
      kind: EntityValuationAction.EDIT_LIABILITY;
      liabilityId: string;
      label: string;
      showDivider?: boolean;
      icon: ComponentType<IconProps>;
    }
  | {
      kind: EntityValuationAction.VIEW_ON_INTEGRATED_PLATFORM;
      name: string;
      integrationEntityId: string;
      showDivider?: boolean;
      icon: ComponentType<IconProps>;
    }
  | {
      kind: EntityValuationAction.RESYNC_ENTITY_VALUATION;
      label: string;
      showDivider?: boolean;
      icon: ComponentType<IconProps>;
    }
  | {
      kind: EntityValuationAction.MANAGE_DATA_LINK;
      label: string;
      showDivider?: boolean;
      icon: ComponentType<IconProps>;
    }
  | {
      kind: EntityValuationAction.MOVE_ASSETS;
      label: string;
      showDivider?: boolean;
      icon: ComponentType<IconProps>;
    };

export interface EntityValuationActionsProps {
  entity: EntityDetail_EntityFragment;
  actions: EntityValuationActionDefinition[];
  onAfterUpdate: () => void;
  isViewOnly?: boolean;
}

const buttonProps = {
  size: 'sm' as const,
  startIcon: Wallet01Icon,
  variant: 'secondary' as const,
  fullWidth: true,
};

interface UseEntityValuationActionsOptions {
  isViewOnly?: boolean;
  /**
   * Called after a holdings update the detail page cares about (e.g. resync, or
   * closing the valuation / edit-liability modal) so it can refetch entity
   * data. Not called on actions that don't update the detail page (cancel of an
   * add/integration/diagram flow).
   */
  onAfterUpdate: () => void;
  /**
   * Called whenever a holdings action's flow ends — any modal dismissed, the
   * integration link opened, etc. A host that *mounts* this flow (the
   * presentation row toolbar) uses it to tear the mount down on every path;
   * the detail page leaves it unset since the trigger stays put.
   */
  onClose?: () => void;
  /**
   * Whether opening the "edit entity" modal records a post-save jump to the
   * entity detail tab (via the {@link NAVIGATE_TO_ENTITY_TAB_AFTER_SAVE_PARAM}
   * query param). Defaults to true (the detail page's behavior). The
   * presentation passes false so saving keeps the user in the designer.
   */
  navigateAfterSave?: boolean;
}

interface EntityValuationActionsController {
  /** Invoke an action — opens the matching modal or fires the mutation. */
  handleAction: (action: EntityValuationActionDefinition) => void;
  /** The modals driven by `handleAction`; render alongside the trigger. */
  modals: ReactNode;
  /**
   * True while the resync mutation is in flight. Resync opens no modal, so a
   * host that mounts this flow (the presentation row toolbar) can show a
   * loading indicator with it instead of rendering nothing until it resolves.
   */
  isResyncing: boolean;
}

/**
 * Encapsulates the holdings-action behavior shared by the entity detail
 * page's "Edit current holdings" button and the presentation entities-list
 * row toolbar: the modal/mutation state, the `handleAction` dispatcher, and
 * the modals themselves. Callers render their own trigger (e.g. a
 * `ButtonWithPopover` of `EntityValuationActionMenuItems`) and drop `modals`
 * into the tree.
 *
 * Reads the active entity/household from `useEntityDetailsContext` /
 * `useHouseholdDetailsContext`, so it must be rendered inside those providers.
 */
export function useEntityValuationActions(
  entity: EntityDetail_EntityFragment,
  {
    isViewOnly = false,
    onAfterUpdate,
    onClose,
    navigateAfterSave = true,
  }: UseEntityValuationActionsOptions
): EntityValuationActionsController {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [resyncEntityValuation, { loading: isResyncing }] =
    useResyncEntityValuationMutation({
      variables: {
        entityId: entity.id,
      },
      onCompleted: () => {
        showFeedback('Initiated resync of valuation data', {
          variant: 'success',
        });
        onAfterUpdate();
      },
      onError: (error) => {
        const userFacingMessages = getUserFacingErrorMessages(error);
        const isHandledError =
          !error.networkError &&
          userFacingMessages.length > 0 &&
          userFacingMessages.length === error.graphQLErrors.length;

        if (isHandledError) {
          showFeedback(
            `Failed to initiate valuation resync: ${userFacingMessages.join(' ')}`
          );
        } else {
          showFeedback(
            'Failed to initiate valuation resync. Please try again.'
          );
          reportError('Failed to initiate valuation resync', error);
        }

        // Resync surfaces no modal, so on failure end the flow too — otherwise a
        // host that mounts it (the presentation row toolbar) stays mounted
        // invisibly with no way to recover.
        onClose?.();
      },
    });
  const [isLiabilityModalOpen, setIsLiabilityModalOpen] = useState(false);
  const [editLiabilityId, setEditLiabilityId] = useState<string | null>(null);
  const [isAssetFullScreenModalOpen, setIsAssetFullScreenModalOpen] =
    useState(false);
  const [isUpdateValuationsModalOpen, setIsUpdateValuationsModalOpen] =
    useState(false);
  const [isLinkIntegrationModalOpen, setIsLinkIntegrationModalOpen] =
    useState(false);
  const [isMoveAssetsDrawerOpen, setIsMoveAssetsDrawerOpen] = useState(false);
  const [initialEditEntitySection, setInitialEditEntitySection] =
    useState<EditEntitySection | null>(null);
  const { entityId, entitySubtypeId, entityType } = useEntityDetailsContext();
  const { householdId } = useHouseholdDetailsContext();
  const [, setSearchParams] = useSearchParams();

  // Record (or clear) which entity-details tab the edit-entity form should jump
  // to after a successful save. Only the entity detail page sets it; the
  // presentation passes navigateAfterSave=false so it stays put.
  const setNavigateAfterSaveTab = useCallback(
    (tab: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab) {
            next.set(NAVIGATE_TO_ENTITY_TAB_AFTER_SAVE_PARAM, tab);
          } else {
            next.delete(NAVIGATE_TO_ENTITY_TAB_AFTER_SAVE_PARAM);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleAction = useCallback(
    (action: EntityValuationActionDefinition) => {
      const actionKind = action.kind;
      switch (actionKind) {
        case EntityValuationAction.MANAGE_VALUATION:
          if (entity.kind === EntityKind.GratTrust) {
            setIsAssetFullScreenModalOpen(true);
            break;
          }

          setIsUpdateValuationsModalOpen(true);
          break;
        case EntityValuationAction.VIEW_ON_INTEGRATED_PLATFORM:
          window.open(
            `/api/v1/integration/redirect/integration-entity?householdId=${householdId}&integrationEntityId=${action.integrationEntityId}`,
            '_blank'
          );
          // Opens a new tab and surfaces no in-app UI; end the flow so a host
          // that mounts it (the presentation row toolbar) tears it down rather
          // than leave it mounted invisibly. Not an update — no refetch.
          onClose?.();
          break;
        case EntityValuationAction.OPEN_ENTITY_EDIT_MODAL:
          setInitialEditEntitySection(action.initialEditEntitySection);
          // Land on the entity-details tab for the edited section after save
          // (unless the host opted out, e.g. the presentation designer).
          setNavigateAfterSaveTab(
            navigateAfterSave && entityType
              ? getActiveTabForSection(
                  entityType,
                  action.initialEditEntitySection
                )
              : null
          );
          break;
        case EntityValuationAction.ADD_LIABILITY:
          setIsLiabilityModalOpen(true);
          break;
        case EntityValuationAction.EDIT_LIABILITY:
          setEditLiabilityId(action.liabilityId);
          setIsLiabilityModalOpen(true);
          break;
        case EntityValuationAction.RESYNC_ENTITY_VALUATION:
          if (entity.pauseIntegrationSync) {
            onClose?.();
            break;
          }
          void resyncEntityValuation();
          break;
        case EntityValuationAction.MANAGE_DATA_LINK:
          setIsLinkIntegrationModalOpen(true);
          break;
        case EntityValuationAction.MOVE_ASSETS:
          setIsMoveAssetsDrawerOpen(true);
          break;
        default:
          throw new UnreachableError({
            case: actionKind,
            message: `Unhandled action: ${actionKind}`,
          });
      }
    },
    [
      entity.kind,
      entity.pauseIntegrationSync,
      householdId,
      resyncEntityValuation,
      onClose,
      navigateAfterSave,
      entityType,
      setNavigateAfterSaveTab,
    ]
  );

  const handleCloseValuationsModal = useCallback(() => {
    setIsUpdateValuationsModalOpen(false);
    onAfterUpdate();
  }, [onAfterUpdate]);

  const modals = (
    <>
      {isLiabilityModalOpen && editLiabilityId === null && (
        <CreateLiabilitySideDrawer
          isOpen={isLiabilityModalOpen}
          entityId={entity.id}
          onClose={() => {
            setIsLiabilityModalOpen(false);
            onClose?.();
          }}
        />
      )}
      {isLiabilityModalOpen && editLiabilityId !== null && (
        <EditLiabilitySideDrawer
          liabilityId={editLiabilityId}
          isOpen={isLiabilityModalOpen}
          entityId={entity.id}
          isViewOnly={isViewOnly}
          onClose={() => {
            setIsLiabilityModalOpen(false);
            setEditLiabilityId(null);
            onAfterUpdate();
          }}
        />
      )}
      {isUpdateValuationsModalOpen &&
        entitySubtypeId &&
        householdId &&
        entityType &&
        entityId && (
          <FullScreenStructuredAssetsModal
            householdId={householdId}
            entityId={entityId}
            subtypeId={entitySubtypeId}
            entityName={entity.subtype.displayName}
            entityType={entityType}
            isOpen={isUpdateValuationsModalOpen}
            onClose={handleCloseValuationsModal}
            headerText={
              isViewOnly ? 'View asset details' : 'Update asset details'
            }
            submitText={
              isViewOnly ? 'View asset details' : 'Update asset details'
            }
          />
        )}
      {isLinkIntegrationModalOpen && (
        <LinkIntegrationEntityFormModal
          entityId={entity.id}
          open
          onClose={() => {
            setIsLinkIntegrationModalOpen(false);
            onAfterUpdate();
            onClose?.();
          }}
        />
      )}
      {entityType && initialEditEntitySection && householdId && entityId && (
        <EditEntitySplitScreenModal
          initialSection={initialEditEntitySection}
          entityType={entityType}
          isOpen={!!initialEditEntitySection}
          onAfterSave={onAfterUpdate}
          onClose={() => {
            setInitialEditEntitySection(null);
            setNavigateAfterSaveTab(null);
            onClose?.();
          }}
        />
      )}
      {isAssetFullScreenModalOpen && (
        <AssetFullScreenModal
          onClose={() => {
            setIsAssetFullScreenModalOpen(false);
            onClose?.();
          }}
          headerText="Update valuation"
          submitCopy="Update valuation"
          entityId={entity.id}
          subtypeId={entity.subtype.id}
          householdId={entity.household.id}
          isOpen={isAssetFullScreenModalOpen}
          assetsSubformType="updateHoldings"
        />
      )}
      {isMoveAssetsDrawerOpen && (
        <MoveAssetsToNewOwnerSideDrawer
          isOpen={isMoveAssetsDrawerOpen}
          householdId={entity.household.id}
          sourceEntityId={entity.id}
          sourceEntityName={entity.subtype.displayName}
          onClose={() => {
            setIsMoveAssetsDrawerOpen(false);
            onClose?.();
          }}
          onMoved={() => {
            setIsMoveAssetsDrawerOpen(false);
            onAfterUpdate();
          }}
        />
      )}
    </>
  );

  return { handleAction, modals, isResyncing };
}

interface EntityValuationActionMenuItemsProps {
  actions: EntityValuationActionDefinition[];
  // Only the integration slice is needed here (to label the resync action with
  // its provider). Narrowed so the presentation toolbar can pass the
  // separately-read integration data without the full holdings fragment.
  entity:
    | (AssetIntegration_EntityFragment & { pauseIntegrationSync: boolean })
    | null
    | undefined;
  onAction: (action: EntityValuationActionDefinition) => void;
}

/**
 * Builds the holdings-action menu items for a `ButtonWithPopover`. Returns an
 * array (not a wrapping element) so callers render them as the popover's
 * direct children, preserving MUI Menu keyboard navigation. Shared by the
 * detail page button and the presentation row toolbar so both surface the
 * same actions (manage valuation, insurance policies, liabilities, resync,
 * view on integrated platform, …).
 */
export function renderEntityValuationActionMenuItems({
  actions,
  entity,
  onAction,
}: EntityValuationActionMenuItemsProps): ReactNode[] {
  return actions.map((action, i) => {
    const icon = (() => {
      if (!action.icon) return null;
      return <action.icon size={18} color={COLORS.TEXT.SECONDARY} />;
    })();

    const menuItemProps: Partial<MenuItemProps> = {
      muiMenuItemProps: {
        divider: action.showDivider,
      },
      icon,
    };

    switch (action.kind) {
      case EntityValuationAction.EDIT_LIABILITY:
      // falls through
      case EntityValuationAction.MANAGE_VALUATION:
        return (
          <MenuItem
            key={i}
            onClick={() => onAction(action)}
            label={action.label}
            {...menuItemProps}
          />
        );
      case EntityValuationAction.ADD_LIABILITY:
        return (
          <MenuItem
            key={i}
            onClick={() => onAction(action)}
            label={action.label}
            {...menuItemProps}
          />
        );
      case EntityValuationAction.OPEN_ENTITY_EDIT_MODAL: {
        return (
          <MenuItem
            key={i}
            onClick={() => onAction(action)}
            label={action.label}
            {...menuItemProps}
          />
        );
      }
      case EntityValuationAction.RESYNC_ENTITY_VALUATION: {
        const provider = getEntityAssetsIntegrationProvider(entity);
        if (!provider) return null; // ts
        const providerName = getIntegrationProviderDisplayName(provider);
        return (
          <MenuItem
            key={i}
            onClick={() => onAction(action)}
            label={`${action.label} from ${providerName}`}
            disabled={entity?.pauseIntegrationSync}
            subtext={
              entity?.pauseIntegrationSync
                ? 'Integration syncing is paused'
                : undefined
            }
            {...menuItemProps}
          />
        );
      }
      case EntityValuationAction.VIEW_ON_INTEGRATED_PLATFORM: {
        // note that menuItemProps is intentionally not passed here because
        // it will never have a divider and we have special icon handling
        return (
          <MenuItem
            key={i}
            icon={<Box width={20} />} // create spacing so this is indented to match other menu items
            onClick={() => onAction(action)}
            iconAfter={icon}
            label={action.name}
          />
        );
      }
      case EntityValuationAction.MANAGE_DATA_LINK:
        return (
          <MenuItem
            key={i}
            onClick={() => onAction(action)}
            label={action.label}
            {...menuItemProps}
          />
        );
      case EntityValuationAction.MOVE_ASSETS:
        return (
          <MenuItem
            key={i}
            onClick={() => onAction(action)}
            label={action.label}
            {...menuItemProps}
          />
        );
    }
  });
}

export function EntityValuationActions({
  entity,
  actions,
  isViewOnly = false,
  onAfterUpdate,
}: EntityValuationActionsProps) {
  const { handleAction, modals } = useEntityValuationActions(entity, {
    isViewOnly,
    onAfterUpdate,
  });

  const actionButton = useMemo(() => {
    return (
      <ButtonWithPopover
        {...buttonProps}
        label={isViewOnly ? 'View current holdings' : 'Edit current holdings'}
        popperVariant="menuBelow"
      >
        {renderEntityValuationActionMenuItems({
          actions,
          entity,
          onAction: handleAction,
        })}
      </ButtonWithPopover>
    );
  }, [actions, entity, handleAction, isViewOnly]);

  return (
    <>
      {modals}
      {actionButton}
    </>
  );
}
